import { useState, FormEvent, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useWhatsAppSettings } from '@/hooks/useWhatsAppSettings';

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    contactNo: '+44 ',
    yearGroup: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('highlight') === 'true';
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlight && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [highlight]);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';
  const { whatsappNumber: WHATSAPP_LINK } = useWhatsAppSettings();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) { setError('Message must be 500 characters or fewer.'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          contactNo: form.contactNo,
          yearGroup: form.yearGroup,
          subject: form.subject,
          message: form.message,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Something went wrong');

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        <section className="relative bg-slate-900 pt-28 pb-14">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=professional%20dark%20navy%20office%20environment%20with%20soft%20ambient%20lighting%2C%20clean%20minimal%20workspace%2C%20academic%20institution%20atmosphere%2C%20no%20people%2C%20abstract%20background&width=1440&height=400&seq=conhero1&orientation=landscape" alt="" className="w-full h-full object-cover object-top opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-slate-900/60"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400 mb-3 block">Get in Touch</span>
            <h1 className="text-4xl font-extrabold text-white mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Contact Us</h1>
            <p className="text-slate-400 text-base max-w-xl">Have a question about our resources, events or syllabus coverage? We&apos;d love to hear from you.</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Info */}
            <div className="flex flex-col gap-6">
              {[
                { icon: 'ri-mail-line', title: 'Email Us', value: 'hi@gcsecomputing.co.uk', sub: 'We reply within 24 hours' },
                { icon: 'ri-map-pin-line', title: 'Location', value: 'United Kingdom', sub: 'Serving UK students nationwide' },
                { icon: 'ri-time-line', title: 'Support Hours', value: 'Mon–Fri, 9am–6pm', sub: 'GMT / BST' },
              ].map((item) => (
                <div key={item.title} className="bg-white rounded-xl border border-slate-100 p-5 flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-emerald-50 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-emerald-700 text-xl`}></i>
                  </div>
                  <div>
                    <p className="text-slate-800 font-semibold text-sm">{item.title}</p>
                    <p className="text-slate-700 text-sm mt-0.5">{item.value}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}

              <a href={`https://wa.me/${WHATSAPP_LINK}`} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] hover:bg-[#1DA851] rounded-xl p-4 flex items-center justify-between text-white transition-colors group cursor-pointer shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-2xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-sm">Start a WhatsApp Chat</p>
                    <p className="text-white/80 text-xs">Fastest way to reach us</p>
                  </div>
                </div>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </a>
              <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5">
                <p className="text-emerald-800 font-semibold text-sm mb-2">Follow Us</p>
                <div className="flex gap-3">
                  {[{ icon: 'ri-youtube-line', label: 'YouTube' }, { icon: 'ri-twitter-x-line', label: 'Twitter' }, { icon: 'ri-instagram-line', label: 'Instagram' }].map((s) => (
                    <a key={s.label} href="#" aria-label={s.label} className="w-9 h-9 flex items-center justify-center bg-white border border-emerald-200 hover:bg-emerald-700 hover:border-emerald-700 rounded-lg transition-colors cursor-pointer group">
                      <i className={`${s.icon} text-emerald-700 group-hover:text-white text-base`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div
              ref={formRef}
              className={`lg:col-span-2 rounded-xl border p-8 transition-all duration-700 ${highlight ? 'bg-emerald-50/60 border-emerald-200 shadow-[0_15px_40px_-10px_rgba(16,185,129,0.15)] -translate-y-1 relative z-10' : 'bg-white border-slate-100'
                }`}
            >
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mb-4">
                    <i className="ri-check-line text-emerald-700 text-3xl"></i>
                  </div>
                  <h3 className="text-slate-800 font-bold text-xl mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>Message Sent!</h3>
                  <p className="text-slate-500 text-sm">Thanks for reaching out. We&apos;ll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <h2 className="text-xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Send a Message</h2>

                  {/* Row 1: Full Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name <span className="text-rose-400">*</span></label>
                      <input name="name" type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address <span className="text-rose-400">*</span></label>
                      <input name="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400 transition-colors" />
                    </div>
                  </div>

                  {/* Row 2: Contact No */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Number <span className="text-slate-400 font-normal text-xs">(optional)</span></label>
                    <input
                      name="contact_no"
                      type="tel"
                      value={form.contactNo}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.startsWith('+44 ')) {
                          setForm({ ...form, contactNo: val });
                        } else if (val.length < 4) {
                          setForm({ ...form, contactNo: '+44 ' });
                        }
                      }}
                      placeholder="+44 7700 000000"
                      maxLength={15}
                      className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400 transition-colors"
                    />
                  </div>

                  {/* Row 3: Education Info — Year Group + Subject */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Education Info</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1.5">Year Group</label>
                        <select name="year_group" value={form.yearGroup} onChange={(e) => setForm({ ...form, yearGroup: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400 transition-colors bg-white cursor-pointer">
                          <option value="">Select year group</option>
                          <option>Year 10</option>
                          <option>Year 11</option>
                          <option>Year 12</option>
                          <option>Year 13</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1.5">Subject</label>
                        <select name="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400 transition-colors bg-white cursor-pointer">
                          <option value="">Select subject</option>
                          <option>Computer Science</option>
                          <option>ICT</option>
                          <option>Programming</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Row 4: Message */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Message <span className="text-rose-400">*</span>
                      <span className="text-slate-400 font-normal ml-1">({form.message.length}/500)</span>
                    </label>
                    <textarea name="message" required rows={5} maxLength={500} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help you?" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400 transition-colors resize-none" />
                  </div>

                  {error && <p className="text-rose-500 text-sm">{error}</p>}
                  <button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-700 text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm">
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
